import { useState } from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '@/components/ui/GlassCard';
import { ExternalLink, Smartphone, Globe } from 'lucide-react';
import { ComingSoonModal } from '@/components/ui/ComingSoonModal';

const projects = [
  {
    id: 1,
    title: 'École360',
    category: 'SaaS - Éducation',
    description: 'Plateforme de gestion scolaire complète avec tableaux de bord intelligents et suivi en temps réel.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/128526920e635c38eb4c93502354627b8687190c395b9359caa033ff2059ec8e.png',
    icon: Globe,
    tags: ['React', 'Node.js', 'MongoDB'],
  },
  {
    id: 2,
    title: 'gencoPro SaaS',
    category: 'Outils professionnels',
    description: 'Suite d\'outils de productivité pour équipes avec collaboration en temps réel et automatisations.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/83a19907837f43a5022d2dd96d3b45b041e50110442d1f811f55a7f395eb4cb0.png',
    icon: Globe,
    tags: ['Vue.js', 'Python', 'PostgreSQL'],
  },
  {
    id: 3,
    title: 'TechGuard AI',
    category: 'Application mobile',
    description: 'Application de cybersécurité alimentée par l\'IA pour la protection des appareils mobiles.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/254bc00b9dab632d3c0d09d50d5ef1eb739702955eca97a13a4f0df0053e516c.png',
    icon: Smartphone,
    tags: ['React Native', 'TensorFlow', 'AWS'],
  },
  {
    id: 4,
    title: 'gencoPro App',
    category: 'Application mobile',
    description: 'Version mobile de gencoPro avec synchronisation cloud et mode hors ligne.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c361eac-5927-49fe-bcdc-358a1c6675cf/images/56e47faa00a296eb3a284c2983e9a59468e4ba494239957dc5584abcd1fa9b83.png',
    icon: Smartphone,
    tags: ['Flutter', 'Firebase', 'GraphQL'],
  },
];

export const Portfolio = () => {
  // L'état (State) pour ouvrir/fermer la modale
  const [isModalOpen, setIsModalOpen] = useState(false);

  // La fonction qui vérifie sur quelle carte on clique
  const handleProjectClick = (projectTitle) => {
    if (projectTitle === 'École360') {
      window.open('https://ecole360.monaliapro.com', '_blank'); // Ouvre le vrai site
    } else {
      setIsModalOpen(true); // Ouvre la modale pour les autres
    }
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-20 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            Nos <span className="text-gradient">Réalisations</span>
          </h2>
          <p className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto">
            Découvrez une sélection de projets qui illustrent notre savoir-faire et notre passion pour l'innovation.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div 
              key={project.id} 
              onClick={() => handleProjectClick(project.title)}
              className="h-full"
            >
              <GlassCard
                data-testid={`portfolio-card-${project.id}`}
                delay={index * 0.15}
                className="group cursor-pointer p-0 overflow-hidden h-full"
              >
                {/* Image */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080410] via-transparent to-transparent opacity-60" />
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                    <project.icon className="w-5 h-5 text-[#4facfe]" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-[#4facfe] text-xs font-medium uppercase tracking-wider">
                        {project.category}
                      </span>
                      <h3 className="font-outfit text-xl sm:text-2xl font-semibold text-white mt-1">
                        {project.title}
                      </h3>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ExternalLink className="w-4 h-4 text-white/70" />
                    </motion.div>
                  </div>

                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-white/70"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </GlassCard>
            </div>
          ))}
        </div>
      </div>

      {/* Intégration de la Modale */}
      <ComingSoonModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </section>
  );
};

export default Portfolio;